// Library
import React, { useEffect, useRef, useState } from 'react';
import { CheckboxField } from '@components/general';
// Components
import { ButtonQHDS } from '@components/general/qldDesignSystem';
import { RxCross1 } from 'react-icons/rx';

// CSS Style for this module
import styles from './playgroundPanelControl.module.scss';

export interface PlaygroundPanelControlOptions {
	showPanel?: boolean;
	isBranchingActive?: boolean;
	hasStepper?: boolean;
	canClickSteps?: boolean;
	canSkipOnStepper?: boolean;
	showNumbersOnStepper?: boolean;
	showTitlesOnStepper?: boolean;
	showDisabledOnStepper?: boolean;
	showDebugButtons?: boolean;
	dataHasExistingApproved?: boolean;
	isServiceActive?: boolean;
}

/**
 * Interface for playground panel control props
 */
interface PlaygroundPanelControlProps {
	options: PlaygroundPanelControlOptions;
	onChangeOptions: (options: PlaygroundPanelControlOptions) => void;
	onClickSave?: () => void;
	onClickRestore?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
	onClickReset?: () => void;
}

/**
 * Playground panel control component to debug Meter Inspection Form.
 * Show in Dev only.
 * @param {PlaygroundPanelControlProps} Prop
 * @returns a div container with the playground controls
 */
export const PlaygroundPanelControl = ({
	options = {},
	onChangeOptions,
	onClickSave,
	onClickRestore,
	onClickReset,
}: PlaygroundPanelControlProps) => {
	// State
	const [showPanel, setShowPanel] = useState(options.showPanel); // initialize by given options
	const [isBranchingActive, setIsBranchingActive] = useState(options.isBranchingActive ?? true);
	const [hasStepper, setHasStepper] = useState(options.hasStepper ?? true);
	const [canClickSteps, setCanClickSteps] = useState(options.canClickSteps ?? false);
	const [canSkipOnStepper, setCanSkipOnStepper] = useState(options.canSkipOnStepper ?? false);
	const [showNumbersOnStepper, setShowNumbersOnStepper] = useState(
		options.showNumbersOnStepper ?? false,
	);
	const [showTitlesOnStepper, setShowTitlesOnStepper] = useState(
		options.showTitlesOnStepper ?? true,
	);
	const [showDisabledOnStepper, setShowDisabledOnStepper] = useState(
		options.showDisabledOnStepper ?? false,
	);
	const [showDebugButtons, setShowDebugButtons] = useState(options.showDebugButtons ?? false);
	// State for data debug
	const [dataHasExistingApproved, setDataHasExistingApproved] = useState(
		options?.dataHasExistingApproved ?? false,
	);
	const [isServiceActive, setIsServiceActive] = useState(options?.isServiceActive ?? true);
	// Ref
	const restoringFileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		options.showPanel = showPanel;
		options.isBranchingActive = isBranchingActive;
		options.hasStepper = hasStepper;
		options.canClickSteps = canClickSteps;
		options.canSkipOnStepper = canSkipOnStepper;
		options.showNumbersOnStepper = showNumbersOnStepper;
		options.showTitlesOnStepper = showTitlesOnStepper;
		options.showDisabledOnStepper = showDisabledOnStepper;
		options.showDebugButtons = showDebugButtons;
		options.dataHasExistingApproved = dataHasExistingApproved;
		options.isServiceActive = isServiceActive;
		onChangeOptions({ ...options });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		showPanel,
		isBranchingActive,
		hasStepper,
		canClickSteps,
		canSkipOnStepper,
		showNumbersOnStepper,
		showTitlesOnStepper,
		showDisabledOnStepper,
		showDebugButtons,
		dataHasExistingApproved,
		isServiceActive,
	]);

	useEffect(() => {
		setDataHasExistingApproved(Boolean(options.dataHasExistingApproved));
		setShowPanel(options.showPanel);
		setIsBranchingActive(Boolean(options.isBranchingActive));
		setHasStepper(Boolean(options.hasStepper));
		setCanClickSteps(Boolean(options.canClickSteps));
		setCanSkipOnStepper(Boolean(options.canSkipOnStepper));
		setShowNumbersOnStepper(Boolean(options.showNumbersOnStepper));
		setShowTitlesOnStepper(Boolean(options.showTitlesOnStepper));
		setShowDisabledOnStepper(Boolean(options.showDisabledOnStepper));
		setShowDebugButtons(Boolean(options.showDebugButtons));
		setDataHasExistingApproved(Boolean(options.dataHasExistingApproved));
		setIsServiceActive(Boolean(options.isServiceActive));
	}, [options, setDataHasExistingApproved]);

	return showPanel ? (
		<div className={styles.playgroundControls}>
			<ButtonQHDS
				onClick={() => setShowPanel(false)}
				type="tertiary"
				customStyles={styles.playgroundClose}
			>
				<RxCross1 />
			</ButtonQHDS>
			<h3>Playground</h3>
			<CheckboxField
				label="Branching options"
				value={isBranchingActive}
				onChange={setIsBranchingActive}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="Stepper"
				value={hasStepper}
				onChange={setHasStepper}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="Clickable steps"
				value={canClickSteps}
				onChange={setCanClickSteps}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="Clickable steps ahead"
				value={canSkipOnStepper}
				onChange={setCanSkipOnStepper}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="Steps numbers"
				value={showNumbersOnStepper}
				onChange={setShowNumbersOnStepper}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="Step titles"
				value={showTitlesOnStepper}
				onChange={setShowTitlesOnStepper}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="Disabled steps"
				value={showDisabledOnStepper}
				onChange={setShowDisabledOnStepper}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="Debug buttons"
				value={showDebugButtons}
				onChange={setShowDebugButtons}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			{/* Progress buttons */}
			<div className={styles.progressButtons}>
				{onClickSave && (
					<ButtonQHDS onClick={onClickSave} type="tertiary">
						Save
					</ButtonQHDS>
				)}
				{onClickRestore && (
					<>
						<ButtonQHDS
							onClick={() => {
								if (restoringFileInputRef.current) {
									restoringFileInputRef?.current?.click();
								}
							}}
							type="tertiary"
						>
							Restore
						</ButtonQHDS>
						<input
							ref={restoringFileInputRef}
							onChange={onClickRestore}
							type="file"
							multiple={false}
							accept=".json"
							hidden
						/>
					</>
				)}
				{onClickReset && (
					<ButtonQHDS onClick={onClickReset} type="tertiary" customStyles={styles.reset}>
						Reset
					</ButtonQHDS>
				)}
			</div>
			{/* Mock of external data from API */}
			<h6>External data</h6>
			<CheckboxField
				label="Existing approved meter by default"
				value={dataHasExistingApproved}
				onChange={setDataHasExistingApproved}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
			<CheckboxField
				label="API service connected"
				value={isServiceActive}
				onChange={setIsServiceActive}
				canDetectOutsideChange
				isLabelClickable
				customStyles={styles.playgroundCheckbox}
			/>
		</div>
	) : (
		<></>
	);
};
